import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdBusiness, MdCheckCircle, MdEdit, MdMedicalServices, MdPerson } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getClaimStatusRecord } from '../../Services/Claim360Service';
import { claimRejectionNote, claimStatusLabel, formatDate, formatMoney, isClaimRejectionLabel } from '../../Utils/stediUtils';
import BackButton from '../../Components/Common/BackButton';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import DataGrid from '../../Components/Table/DataGrid';
import { FieldGrid, formatDateTime, Section, ServiceLineFields, STATUS_STYLE } from '../../Components/Claim360/StediViewParts';

// Claim status label -> status pill tone; anything else (Pending, Info Requested, Not Found) is a warning.
const LABEL_TONES = { Finalized: 'success', Denied: 'danger', Rejected: 'danger', Error: 'danger', 'Payer Unavailable': 'warning' };

// The payer's category and reason; for a rejection that names no field, also where to find which one.
const claimStatusText = (claim) => [
    [claim.statusCategoryCodeValue, claim.statusCodeValue].filter(Boolean).join(' - '),
    claimRejectionNote(claim),
].filter(Boolean).join(' ') || claim.statusCategoryCode || '-';

const CLAIM_COLUMNS = [
    { id: 'tradingPartnerClaimNumber', label: 'Payer Claim #', minWidth: 140, format: (value) => value || '-' },
    // accessor drives sorting and filtering; format is what the cell shows.
    { id: 'status', label: 'Status', minWidth: 240, accessor: claimStatusText, format: (_value, claim) => claimStatusText(claim) },
    {
        id: 'serviceDate',
        label: 'Service Date',
        minWidth: 130,
        format: (_value, claim) => formatDate(claim.claimServiceDate || claim.claimServiceBeginDate),
    },
    { id: 'submittedAmount', label: 'Submitted', minWidth: 110, align: 'right', format: (value) => formatMoney(value) },
    { id: 'amountPaid', label: 'Paid', minWidth: 110, align: 'right', format: (value) => formatMoney(value) },
    // The payer's adjudication date, also sent when nothing was paid.
    { id: 'paidDate', label: 'Finalized On', minWidth: 120, format: (value) => formatDate(value) },
];

export function ClaimStatusView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );

    const canEdit = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_LIST, decodedToken);
    const [claimStatus, setClaimStatus] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!token) return undefined;
        let cancelled = false;
        setLoading(true);
        getClaimStatusRecord(id, token)
            .then((response) => {
                if (!cancelled) setClaimStatus(response?.data ?? null);
            })
            .catch((error) => {
                console.error('Failed to load claim status:', error);
                if (!cancelled) {
                    setClaimStatus(null);
                    toast.error(error.message || 'Failed to load claim status.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, token]);

    if (loading) {
        return (
            <div className="spinner position-absolute d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!claimStatus) {
        return (
            <ViewEmptyState
                title="Claim Status Not Found"
                description="This claim status record is no longer available."
                icon={MdCheckCircle}
                canViewList={canViewList}
                backRoute={APPRoutes.CLAIM_STATUS}
                backLabel="Back to Claim Statuses"
            />
        );
    }

    const status = claimStatusLabel(claimStatus);
    const payer = claimStatus.payerName
        ? `${claimStatus.payerName}${claimStatus.payerId ? ` (${claimStatus.payerId})` : ''}`
        : claimStatus.payerId;
    const checkedBy = [claimStatus.checkedByFirstName, claimStatus.checkedByLastName].filter(Boolean).join(' ');
    const claims = (claimStatus.response?.claims ?? []).map((claim) => claim?.claimStatus ?? {});

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton label="Back to Claim Statuses" onClick={() => navigate(APPRoutes.CLAIM_STATUS)} />
                )}
                {canEdit && (
                    <button
                        type="button"
                        className="btn hc-bg-primary text-white rounded-3 fw-semibold d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(APPRoutes.CLAIM_STATUS_EDIT.replace(':id', id), { state: { claimStatus } })}
                    >
                        <MdEdit size={18} /> Re-check Claim Status
                    </button>
                )}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <span
                            className="px-3 py-2 rounded-pill fw-semibold d-inline-block mb-3"
                            style={{ fontSize: '0.82rem', ...STATUS_STYLE[LABEL_TONES[status] ?? (isClaimRejectionLabel(status) ? 'danger' : 'warning')] }}
                        >
                            {status}
                        </span>
                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {claimStatus.patientName || '-'}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)' }}>
                            Member ID: {claimStatus.memberId || '-'} · DOB: {formatDate(claimStatus.dateOfBirth)}
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Payer: {payer || '-'}</p>
                    </div>
                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Organization</p>
                            <p className="fw-bold text-white mb-0 fs-5">{claimStatus.organizationName || '-'}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12 col-lg-6">
                    <Section icon={MdPerson} title="Patient" className="h-100">
                        <FieldGrid fields={[
                            ['Name', claimStatus.patientName],
                            ['Date of Birth', formatDate(claimStatus.dateOfBirth)],
                            ['Member ID', claimStatus.memberId],
                            ['Payer', payer],
                        ]}
                        />
                    </Section>
                </div>
                <div className="col-12 col-lg-6">
                    <Section icon={MdBusiness} title="Check" className="h-100">
                        <FieldGrid fields={[
                            ['Payer Claim Number', claimStatus.payerClaimNumber],
                            ['Status Category Codes', (claimStatus.statusCategoryCodes ?? []).join(', ')],
                            ['Claims Returned', claimStatus.claimCount != null ? String(claimStatus.claimCount) : null],
                            ['Checked By', checkedBy],
                            ['Checked On', formatDateTime(claimStatus.checkedOn)],
                        ]}
                        />
                    </Section>
                </div>
            </div>

            {claimStatus.procedureCode && claims.length > 0 && (
                <Section icon={MdMedicalServices} title={`Service Line ${claimStatus.procedureCode}`} className="mb-4">
                    <ServiceLineFields
                        response={claimStatus.response}
                        procedureCode={claimStatus.procedureCode}
                        chargeAmount={claimStatus.lineChargeAmount}
                        colClass="col-12 col-sm-6 col-lg-4"
                    />
                </Section>
            )}

            {claims.length > 0 && (
                <Section icon={MdCheckCircle} title={`Claims (${claims.length})`} className="mb-4">
                    <DataGrid
                        columns={CLAIM_COLUMNS}
                        rows={claims}
                        defaultRowsPerPage={10}
                        disableColumnMenu
                        getRowId={(claim, index) => claim.tradingPartnerClaimNumber ?? index}
                    />
                </Section>
            )}
        </div>
    );
}

export default ClaimStatusView;
